import type { CSSProperties } from 'react';

import classNames from 'classnames';

import type { TTextLeaf } from '@/textLeaf';
import { getTextColor } from '@/textLeaf/utils';
import { generatePositionStyle, type PositionStyle } from '@/utils/positioning';

import { getTextAlignStyleVariables } from '../utils/typography';
import { useAnimatedBlock } from '../utils/useAnimatedBlock';

import { HEADING_DEFAULTS } from './Heading.constants';
import * as styles from './Heading.css';
import type { HeadingElementProps } from './Heading.types';

const getBaseColor = (element: HeadingElementProps['element']) => {
  const { content } = element;

  // we want to get either the first child or the last child's
  // color attribute.
  const firstChild = content?.[0];
  const lastChild = content?.[content.length - 1];

  if (!firstChild || !lastChild) return 'inherit';

  const firstChildColor = !!firstChild && getTextColor(firstChild as TTextLeaf);
  const lastChildColor = !!lastChild && getTextColor(lastChild as TTextLeaf);

  return firstChildColor || lastChildColor || '';
};

// Keeping this as inherit could have unintended consequences with older templates so let's force to be transparent on heading elements
const getBackgroundColor = (backgroundColor: string) => {
  if (backgroundColor === 'inherit') return 'transparent';
  return backgroundColor;
};

export function Heading1Element(props: HeadingElementProps) {
  const { element, children, attributes, className, ...rest } = props;
  const {
    width,
    maxWidth,
    textAlign,
    mobileTextAlign,
    mobile_hide,
    desktop_hide,
    animation,
    padding,
    mobilePadding,
    letterSpacing,
    mobileLineHeight,
    lineHeight,
    margin,
    backgroundColor,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    customAttributes,
    lineClampClass,
    textTransform,
  } = element.attrs || {};

  const hasChild = !!element.content?.[0];

  const hasFontSize = hasChild && 'fontSize' in element.content[0];
  const hasFontWeight = hasChild && 'fontWeight' in element.content[0];
  const hasLetterSpacing = hasChild && 'letterSpacing' in element.content[0];

  const [marginTop, marginRight, marginBottom, marginLeft] = (margin ?? HEADING_DEFAULTS.margin).toString().split(' ');
  const [ref, animatedClassname] = useAnimatedBlock(animation);
  return (
    <h1
      ref={ref}
      {...rest}
      {...customAttributes}
      className={classNames(
        className,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
        },
        animatedClassname,
        styles.heading,
        {
          [styles.h1Element]: !(hasFontSize && hasFontWeight && hasLetterSpacing),
        },
        customAttributes?.className,
        lineClampClass,
      )}
      style={
        {
          ...generatePositionStyle(element.attrs as PositionStyle),
          width,
          maxWidth,
          '--base-color': getBaseColor(element),
          '--letter-spacing': letterSpacing ?? HEADING_DEFAULTS.letterSpacing,
          '--padding': padding ?? HEADING_DEFAULTS.padding,
          '--mobile-padding': mobilePadding ?? HEADING_DEFAULTS.padding,
          '--padding-root': padding ?? HEADING_DEFAULTS.paddingRoot,
          '--margin-top': marginTop,
          '--margin-right': marginRight,
          '--margin-bottom': marginBottom,
          '--margin-left': marginLeft,
          '--margin': margin ?? HEADING_DEFAULTS.margin,
          '--line-height': lineHeight,
          '--mobile-line-height': mobileLineHeight,
          '--background-color': backgroundColor ? getBackgroundColor(backgroundColor) : 'transparent',
          '--border-radius': borderRadius,
          '--border-width': borderWidth,
          '--border-color': borderColor,
          '--border-style': borderStyle,
          '--text-transform': textTransform,
          ...getTextAlignStyleVariables({
            mobileAlign: mobileTextAlign || textAlign,
            desktopAlign: textAlign,
          }),
        } as CSSProperties
      }
      {...attributes}
    >
      {children}
    </h1>
  );
}

export function Heading2Element(props: HeadingElementProps) {
  const { element, children, attributes, className, ...rest } = props;
  const {
    width,
    maxWidth,
    textAlign,
    mobile_hide,
    desktop_hide,
    animation,
    padding,
    mobilePadding,
    margin,
    customAttributes,
    lineHeight,
    mobileLineHeight,
    mobileTextAlign,
    backgroundColor,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    lineClampClass,
    textTransform,
    letterSpacing,
  } = element.attrs || {};

  const hasChild = !!element.content?.[0];

  const hasFontSize = hasChild && 'fontSize' in element.content[0];
  const hasFontWeight = hasChild && 'fontWeight' in element.content[0];
  const hasLetterSpacing = hasChild && 'letterSpacing' in element.content[0];

  const [marginTop, marginRight, marginBottom, marginLeft] = (margin ?? HEADING_DEFAULTS.margin).toString().split(' ');
  const [ref, animatedClassname] = useAnimatedBlock(animation);
  return (
    <h2
      ref={ref}
      {...rest}
      {...customAttributes}
      className={classNames(
        className,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
        },
        animatedClassname,
        styles.heading,
        {
          [styles.h2Element]: !(hasFontSize && hasFontWeight && hasLetterSpacing),
        },
        customAttributes?.className,
        lineClampClass,
      )}
      style={
        {
          width,
          maxWidth,
          '--base-color': getBaseColor(element),
          '--letter-spacing': letterSpacing ?? HEADING_DEFAULTS.letterSpacing,
          '--padding': padding ?? HEADING_DEFAULTS.padding,
          '--mobile-padding': mobilePadding ?? HEADING_DEFAULTS.padding,
          '--padding-root': padding ?? HEADING_DEFAULTS.paddingRoot,
          '--margin-top': marginTop,
          '--margin-right': marginRight,
          '--margin-bottom': marginBottom,
          '--margin-left': marginLeft,
          '--margin': margin ?? HEADING_DEFAULTS.margin,
          '--line-height': lineHeight,
          '--mobile-line-height': mobileLineHeight,
          '--background-color': backgroundColor ? getBackgroundColor(backgroundColor) : 'transparent',
          '--border-radius': borderRadius,
          '--border-width': borderWidth,
          '--border-color': borderColor,
          '--border-style': borderStyle,
          '--text-transform': textTransform,
          ...getTextAlignStyleVariables({
            mobileAlign: mobileTextAlign || textAlign,
            desktopAlign: textAlign,
          }),
        } as CSSProperties
      }
      {...attributes}
    >
      {children}
    </h2>
  );
}

export function Heading3Element(props: HeadingElementProps) {
  const { element, children, attributes, className, ...rest } = props;
  const {
    width,
    maxWidth,
    textAlign,
    mobile_hide,
    desktop_hide,
    animation,
    padding,
    mobilePadding,
    margin,
    customAttributes,
    lineHeight,
    mobileLineHeight,
    mobileTextAlign,
    backgroundColor,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    lineClampClass,
    textTransform,
    letterSpacing,
  } = element.attrs || {};

  const hasChild = !!element.content?.[0];

  const hasFontSize = hasChild && 'fontSize' in element.content[0];
  const hasFontWeight = hasChild && 'fontWeight' in element.content[0];
  const hasLetterSpacing = hasChild && 'letterSpacing' in element.content[0];

  const [marginTop, marginRight, marginBottom, marginLeft] = (margin ?? HEADING_DEFAULTS.margin).toString().split(' ');
  const [ref, animatedClassname] = useAnimatedBlock(animation);
  return (
    <h3
      ref={ref}
      {...rest}
      {...customAttributes}
      className={classNames(
        className,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
        },
        animatedClassname,
        styles.heading,
        {
          [styles.h3Element]: !(hasFontSize && hasFontWeight && hasLetterSpacing),
        },
        customAttributes?.className,
        lineClampClass,
      )}
      style={
        {
          width,
          maxWidth,
          '--base-color': getBaseColor(element),
          '--letter-spacing': letterSpacing ?? HEADING_DEFAULTS.letterSpacing,
          '--padding': padding ?? HEADING_DEFAULTS.padding,
          '--mobile-padding': mobilePadding ?? HEADING_DEFAULTS.padding,
          '--padding-root': padding ?? HEADING_DEFAULTS.paddingRoot,
          '--margin-top': marginTop,
          '--margin-right': marginRight,
          '--margin-bottom': marginBottom,
          '--margin-left': marginLeft,
          '--margin': margin ?? HEADING_DEFAULTS.margin,
          '--line-height': lineHeight,
          '--mobile-line-height': mobileLineHeight,
          '--background-color': backgroundColor ? getBackgroundColor(backgroundColor) : 'transparent',
          '--border-radius': borderRadius,
          '--border-width': borderWidth,
          '--border-color': borderColor,
          '--border-style': borderStyle,
          '--text-transform': textTransform,
          ...getTextAlignStyleVariables({
            mobileAlign: mobileTextAlign || textAlign,
            desktopAlign: textAlign,
          }),
        } as CSSProperties
      }
      {...attributes}
    >
      {children}
    </h3>
  );
}

export function Heading4Element(props: HeadingElementProps) {
  const { element, children, attributes, className, ...rest } = props;
  const {
    width,
    maxWidth,
    textAlign,
    mobile_hide,
    desktop_hide,
    animation,
    padding,
    mobilePadding,
    margin,
    customAttributes,
    lineHeight,
    mobileLineHeight,
    mobileTextAlign,
    backgroundColor,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    lineClampClass,
    textTransform,
    letterSpacing,
  } = element.attrs || {};

  const hasChild = !!element.content?.[0];

  const hasFontSize = hasChild && 'fontSize' in element.content[0];
  const hasFontWeight = hasChild && 'fontWeight' in element.content[0];
  const hasLetterSpacing = hasChild && 'letterSpacing' in element.content[0];

  const [marginTop, marginRight, marginBottom, marginLeft] = (margin ?? HEADING_DEFAULTS.margin).toString().split(' ');
  const [ref, animatedClassname] = useAnimatedBlock(animation);
  return (
    <h4
      ref={ref}
      {...rest}
      {...customAttributes}
      className={classNames(
        className,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
        },
        animatedClassname,
        styles.heading,
        {
          [styles.h4Element]: !(hasFontSize && hasFontWeight && hasLetterSpacing),
        },
        customAttributes?.className,
        lineClampClass,
      )}
      style={
        {
          width,
          maxWidth,
          '--base-color': getBaseColor(element),
          '--letter-spacing': letterSpacing ?? HEADING_DEFAULTS.letterSpacing,
          '--padding': padding ?? HEADING_DEFAULTS.padding,
          '--mobile-padding': mobilePadding ?? HEADING_DEFAULTS.padding,
          '--padding-root': padding ?? HEADING_DEFAULTS.paddingRoot,
          '--margin-top': marginTop,
          '--margin-right': marginRight,
          '--margin-bottom': marginBottom,
          '--margin-left': marginLeft,
          '--margin': margin ?? HEADING_DEFAULTS.margin,
          '--line-height': lineHeight,
          '--mobile-line-height': mobileLineHeight,
          '--background-color': backgroundColor ? getBackgroundColor(backgroundColor) : 'transparent',
          '--border-radius': borderRadius,
          '--border-width': borderWidth,
          '--border-color': borderColor,
          '--border-style': borderStyle,
          '--text-transform': textTransform,
          ...getTextAlignStyleVariables({
            mobileAlign: mobileTextAlign || textAlign,
            desktopAlign: textAlign,
          }),
        } as CSSProperties
      }
      {...attributes}
    >
      {children}
    </h4>
  );
}

export function Heading5Element(props: HeadingElementProps) {
  const { element, children, attributes, className, ...rest } = props;
  const {
    width,
    maxWidth,
    textAlign,
    mobile_hide,
    desktop_hide,
    animation,
    padding,
    margin,
    customAttributes,
    lineHeight,
    mobileLineHeight,
    mobileTextAlign,
    mobilePadding,
    backgroundColor,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    lineClampClass,
    textTransform,
    letterSpacing,
  } = element.attrs || {};

  const hasChild = !!element.content?.[0];

  const hasFontSize = hasChild && 'fontSize' in element.content[0];
  const hasFontWeight = hasChild && 'fontWeight' in element.content[0];
  const hasLetterSpacing = hasChild && 'letterSpacing' in element.content[0];

  const [marginTop, marginRight, marginBottom, marginLeft] = (margin ?? HEADING_DEFAULTS.margin).toString().split(' ');
  const [ref, animatedClassname] = useAnimatedBlock(animation);
  return (
    <h5
      ref={ref}
      {...rest}
      {...customAttributes}
      className={classNames(
        className,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
        },
        animatedClassname,
        styles.heading,
        {
          [styles.h5Element]: !(hasFontSize && hasFontWeight && hasLetterSpacing),
        },
        customAttributes?.className,
        lineClampClass,
      )}
      style={
        {
          width,
          maxWidth,
          '--base-color': getBaseColor(element),
          '--letter-spacing': letterSpacing ?? HEADING_DEFAULTS.letterSpacing,
          '--padding': padding ?? HEADING_DEFAULTS.padding,
          '--mobile-padding': mobilePadding ?? HEADING_DEFAULTS.padding,
          '--padding-root': padding ?? HEADING_DEFAULTS.paddingRoot,
          '--margin-top': marginTop,
          '--margin-right': marginRight,
          '--margin-bottom': marginBottom,
          '--margin-left': marginLeft,
          '--margin': margin ?? HEADING_DEFAULTS.margin,
          '--line-height': lineHeight,
          '--mobile-line-height': mobileLineHeight,
          '--background-color': backgroundColor ? getBackgroundColor(backgroundColor) : 'transparent',
          '--border-radius': borderRadius,
          '--border-width': borderWidth,
          '--border-color': borderColor,
          '--border-style': borderStyle,
          '--text-transform': textTransform,
          ...getTextAlignStyleVariables({
            mobileAlign: mobileTextAlign || textAlign,
            desktopAlign: textAlign,
          }),
        } as CSSProperties
      }
      {...attributes}
    >
      {children}
    </h5>
  );
}

export function Heading6Element(props: HeadingElementProps) {
  const { element, children, attributes, className, ...rest } = props;
  const {
    width,
    maxWidth,
    textAlign,
    mobile_hide,
    desktop_hide,
    animation,
    padding,
    margin,
    customAttributes,
    lineHeight,
    mobileLineHeight,
    mobileTextAlign,
    mobilePadding,
    backgroundColor,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    lineClampClass,
    textTransform,
    letterSpacing,
  } = element.attrs || {};

  const hasChild = !!element.content?.[0];

  const hasFontSize = hasChild && 'fontSize' in element.content[0];
  const hasFontWeight = hasChild && 'fontWeight' in element.content[0];
  const hasLetterSpacing = hasChild && 'letterSpacing' in element.content[0];

  const [marginTop, marginRight, marginBottom, marginLeft] = (margin ?? HEADING_DEFAULTS.margin).toString().split(' ');
  const [ref, animatedClassname] = useAnimatedBlock(animation);

  return (
    <h6
      ref={ref}
      {...rest}
      {...customAttributes}
      className={classNames(
        className,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
        },
        animatedClassname,
        styles.heading,
        {
          [styles.h6Element]: !(hasFontSize && hasFontWeight && hasLetterSpacing),
        },
        customAttributes?.className,
        lineClampClass,
      )}
      style={
        {
          width,
          maxWidth,
          '--base-color': getBaseColor(element),
          '--letter-spacing': letterSpacing ?? HEADING_DEFAULTS.letterSpacing,
          '--padding': padding ?? HEADING_DEFAULTS.padding,
          '--mobile-padding': mobilePadding ?? padding ?? HEADING_DEFAULTS.padding,
          '--padding-root': padding ?? HEADING_DEFAULTS.paddingRoot,
          '--margin-top': marginTop,
          '--margin-right': marginRight,
          '--margin-bottom': marginBottom,
          '--margin-left': marginLeft,
          '--margin': margin ?? HEADING_DEFAULTS.margin,
          '--line-height': lineHeight,
          '--mobile-line-height': mobileLineHeight,
          '--background-color': backgroundColor ? getBackgroundColor(backgroundColor) : 'transparent',
          '--border-radius': borderRadius,
          '--border-width': borderWidth,
          '--border-color': borderColor,
          '--border-style': borderStyle,
          '--text-transform': textTransform,
          ...getTextAlignStyleVariables({
            mobileAlign: mobileTextAlign || textAlign,
            desktopAlign: textAlign,
          }),
        } as CSSProperties
      }
      {...attributes}
    >
      {children}
    </h6>
  );
}
